import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useProvider } from "../hooks/useProvider";
import ProviderSelector from "./common/ProviderSelector";
import api from "../services/api";
import "./Navbar.css";
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const AppNavbar = () => {
  const { user, logout } = useAuth();
  const { availableProviders } = useProvider();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuItems, setMenuItems] = useState([]);
  const [logo, setLogo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const fetchMenuData = async () => {
      try {
        const [menuResponse, logoResponse] = await Promise.all([
          api.get("/menu/items").catch(() => ({ data: [] })),
          api.get("/menu/logo").catch(() => ({ data: { logo: null } })),
        ]);
        setMenuItems(menuResponse.data || []);
        const logoData = logoResponse.data?.logo || null;
        setLogo(logoData);
      } catch (error) {
        console.error("Error fetching menu data:", error);
        setMenuItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMenuData();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isUserMenuOpen && !event.target.closest(".navbar-user-menu")) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isUserMenuOpen]);

  const handleLogout = () => {
    setIsUserMenuOpen(false);
    logout();
    navigate("/");
  };

  const handleLogin = () => {
    setIsUserMenuOpen(false);
    navigate("/login");
  };

  const getLogoUrl = () => {
    if (!logo || !logo.path) return null;
    const baseUrl = API_URL.endsWith("/api")
      ? API_URL.slice(0, -4)
      : API_URL.replace("/api", "");
    return `${baseUrl}${logo.path}`;
  };

  const logoUrl = getLogoUrl();
  const displayMenuItems =
    !loading && menuItems.length > 0
      ? menuItems
      : !loading
      ? [
          { id: 1, text: "Dashboard", link: "/" },
          { id: 2, text: "VPS Servers", link: "/servers" },
          { id: 3, text: "Spaces", link: "/spaces" },
          { id: 4, text: "Firewalls", link: "/firewalls" },
        ]
      : [];

  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  return (
    <nav className={`modern-navbar ${scrolled ? "scrolled" : ""}`}>
      <div className="navbar-container">
        <Link to="https://cyberin.in" className="navbar-brand">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Cyberin Cloud"
              className="navbar-logo"
              onError={(e) => {
                console.error("Failed to load logo image:", logoUrl);
                e.target.style.display = "none";
              }}
            />
          ) : (
            <span className="navbar-brand-text">Cloud Platform</span>
          )}
        </Link>

        <button
          className={`mobile-menu-toggle ${isMobileMenuOpen ? "active" : ""}`}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        <div className={`navbar-menu ${isMobileMenuOpen ? "open" : ""}`}>
          <div className="navbar-nav">
            {displayMenuItems.map((item) => (
              <Link
                key={item.id}
                to={item.link}
                className={`nav-link ${isActive(item.link) ? "active" : ""}`}
              >
                <span className="nav-link-text">{item.text}</span>
              </Link>
            ))}
          </div>

          <div className="navbar-actions">
            {availableProviders.length > 1 && (
              <div className="navbar-provider-selector">
                <ProviderSelector showLabel={false} />
              </div>
            )}
            <div className="navbar-user-menu">
              <button
                className="user-menu-button"
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              >
                <span className="user-menu-text">
                  {user?.name
                    ? user.name
                    : user?.email || (user ? "User" : "Sign in")}
                </span>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={`user-menu-arrow ${isUserMenuOpen ? "open" : ""}`}
                >
                  <path
                    d="M3 4.5L6 7.5L9 4.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <div
                className={`user-menu-dropdown ${isUserMenuOpen ? "open" : ""}`}
              >
                {user ? (
                  <>
                    <Link
                      to="/orders"
                      className="user-menu-item"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      Orders
                    </Link>
                    <button onClick={handleLogout} className="user-menu-item">
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={handleLogin}
                      className="user-menu-item"
                    >
                      Login
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default AppNavbar;
